/**
 * Запасной вход, когда пароль забыт.
 *
 * Дверь без запасного ключа однажды закроется навсегда — и человек останется
 * без записей о приёме лекарств, потому что забыл четыре цифры. Поэтому здесь
 * спрашивается пароль аккаунта: он у человека и так есть, вводится он редко и
 * потому не забывается вместе с паролем на вход.
 *
 * Верный пароль аккаунта **убирает пароль на вход целиком**, а не открывает
 * разово. Иначе человек оказался бы в том же тупике при следующем запуске.
 */

import { LifeBuoy } from 'lucide-react';
import { useState } from 'react';

import { Button, Input, Sheet } from '@/shared/ui';

interface ForgotPinSheetProps {
  email: string;
  onClose: () => void;
  /** Проверяет пароль аккаунта. `false` — не подошёл. */
  onSubmit: (password: string) => Promise<boolean>;
}

export function ForgotPinSheet({ email, onClose, onSubmit }: ForgotPinSheetProps) {
  const [password, setPassword] = useState('');
  const [wrong, setWrong] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      setWrong(!(await onSubmit(password)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title="Забыли пароль"
      description="Пароль от аккаунта уберёт его"
      footer={
        <Button
          block
          size="lg"
          loading={busy}
          disabled={password === ''}
          onClick={() => void submit()}
        >
          Убрать пароль
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="flex items-start gap-3 text-[13px] leading-relaxed text-muted">
          <LifeBuoy size={18} className="mt-0.5 shrink-0 text-subtle" aria-hidden />
          <span>
            Введите пароль от аккаунта {email}. Пароль на вход будет убран — новый можно задать в
            настройках. Данные при этом остаются на месте.
          </span>
        </p>

        <Input
          label="Пароль от аккаунта"
          type="password"
          autoComplete="current-password"
          value={password}
          error={wrong ? 'Пароль не подходит' : undefined}
          onChange={(event) => {
            setPassword(event.target.value);
            setWrong(false);
          }}
        />
      </div>
    </Sheet>
  );
}
